import React, { useState, useEffect, useRef } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

export default function MainServiceFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors]       = useState({});
  const [editData, setEditData]   = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef              = useRef(null);
  const { userid } = useSession();

  // ✅ Fetch record by ID when editing
  useEffect(() => {
    if (mode === "edit" && userData?.id) {
      const fetchById = async () => {
        try {
          const form = new FormData();
          form.append("action", "GETBYID");
          form.append("id", userData.id);
          form.append("userid", userid);
          const res    = await fetch("https://pro.mokshiq.in/main_service_action", {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            body: form,
          });
          const result = await res.json();
          if (result.status) {
            setEditData(result.data);
            setImagePreview(result.data.image || null);
          }
        } catch (err) { console.error(err); }
      };
      fetchById();
    } else {
      setEditData(null);
      setImageFile(null);
      setImagePreview(null);
    }
  }, [mode, userData]);

  // ✅ Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!document.getElementById("name")?.value.trim())     newErrors.name     = "Name is required.";
    if (!document.getElementById("category")?.value.trim()) newErrors.category = "Category is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) { showMainToast("Please correct the errors before saving.", "danger"); return; }

    const formData = new FormData();
    formData.append("name",           document.getElementById("name").value.trim());
    formData.append("category",       document.getElementById("category").value.trim());
    formData.append("material_click", document.getElementById("material_click").value.trim());
    formData.append("web_url",        document.getElementById("web_url").value.trim());
    formData.append("id",             userData?.id || "");
    formData.append("action",         "ADDUpdate");
    formData.append("userid",         userid);

    // ✅ Append image only if new one selected
    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      const response = await fetch("https://pro.mokshiq.in/main_service_action", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData,
      });
      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch { showMainToast("Something went wrong!", "danger"); }
  };

  const isReady = mode === "add" || (mode === "edit" && editData !== null);

  return (
    <BaseModal
      show={show} onClose={onClose}
      title={mode === "edit" ? "Edit Service" : "Add Service"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        { label: mode === "edit" ? "Update" : "Save", className: "btn-primary", onClick: handleSave },
      ]}
    >
      <div className="card-body">
        {!isReady ? <div className="text-center py-4">Loading...</div> : (
          <form key={editData?.id || "new"} className="row g-3 app-form rounded-control">

            {/* Name */}
            <div className="col-md-6">
              <label className="form-label">Name</label>
              <input
                id="name"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                defaultValue={editData?.name || ""}
              />
              {errors.name && <div className="text-danger">{errors.name}</div>}
            </div>

            {/* Category */}
            <div className="col-md-6">
              <label className="form-label">Category</label>
              <input
                id="category"
                className={`form-control ${errors.category ? "is-invalid" : ""}`}
                defaultValue={editData?.category || ""}
              />
              {errors.category && <div className="text-danger">{errors.category}</div>}
            </div>

            {/* Material Click */}
            <div className="col-md-6">
              <label className="form-label">Material Click</label>
              <input
                id="material_click"
                className="form-control"
                defaultValue={editData?.material_click || ""}
              />
            </div>

            {/* Web URL */}
            <div className="col-md-6">
              <label className="form-label">Web URL</label>
              <input
                id="web_url"
                className="form-control"
                defaultValue={editData?.web_url || ""}
              />
            </div>

            {/* Image Upload */}
            <div className="col-md-12">
              <label className="form-label">Image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="form-control"
                onChange={handleImageChange}
              />
              {/* ✅ Image preview */}
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger ms-2"
                    onClick={() => { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  >
                    Remove
                  </button>
                </div>
              )}
              {mode === "edit" && !imagePreview && (
                <small className="text-muted">Leave empty to keep existing image.</small>
              )}
            </div>

          </form>
        )}
      </div>
    </BaseModal>
  );
}